<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  timeline: { type: Array, default: () => [] },
});

const { t } = useI18n();

const canvasRef = ref(null);
const activeMetrics = ref(['sent', 'delivered', 'read']);

const COLORS = {
  sent: '#322F23',
  delivered: '#3b82f6',
  read: '#8b5cf6',
};

const metrics = [
  { key: 'sent', label: 'Sent', color: COLORS.sent },
  { key: 'delivered', label: 'Delivered', color: COLORS.delivered },
  { key: 'read', label: 'Read', color: COLORS.read },
];

const toggleMetric = key => {
  if (activeMetrics.value.includes(key)) {
    if (activeMetrics.value.length > 1) {
      activeMetrics.value = activeMetrics.value.filter(m => m !== key);
    }
  } else {
    activeMetrics.value = [...activeMetrics.value, key];
  }
};

const maxValue = computed(() => {
  if (!props.timeline.length) return 0;
  let max = 0;
  props.timeline.forEach(bucket => {
    activeMetrics.value.forEach(key => {
      if (bucket[key] > max) max = bucket[key];
    });
  });
  return max || 1;
});

const drawChart = () => {
  const canvas = canvasRef.value;
  if (!canvas || !props.timeline.length) return;

  const ctx = canvas.getContext('2d');
  const rect = canvas.parentElement.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  canvas.width = rect.width * dpr;
  canvas.height = 200 * dpr;
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = '200px';
  ctx.scale(dpr, dpr);

  const w = rect.width;
  const h = 200;
  const padding = { top: 10, right: 10, bottom: 25, left: 40 };
  const chartW = w - padding.left - padding.right;
  const chartH = h - padding.top - padding.bottom;

  ctx.clearRect(0, 0, w, h);

  // Y axis labels
  ctx.fillStyle = '#94a3b8';
  ctx.font = '10px sans-serif';
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i += 1) {
    const val = Math.round((maxValue.value / 4) * i);
    const y = padding.top + chartH - (chartH / 4) * i;
    ctx.fillText(val, padding.left - 5, y + 3);
  }

  // X axis labels
  ctx.textAlign = 'center';
  const data = props.timeline;
  const step = Math.max(1, Math.floor(data.length / 5));
  data.forEach((bucket, i) => {
    if (i % step === 0) {
      const x = padding.left + (chartW / (data.length - 1)) * i;
      const date = new Date(bucket.time);
      ctx.fillText(
        `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`,
        x,
        h - 5
      );
    }
  });

  // Draw area charts
  activeMetrics.value.forEach(metricKey => {
    const color = COLORS[metricKey];
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top + chartH);

    data.forEach((bucket, i) => {
      const x = padding.left + (chartW / Math.max(data.length - 1, 1)) * i;
      const y =
        padding.top + chartH - (bucket[metricKey] / maxValue.value) * chartH;
      if (i === 0) ctx.lineTo(x, y);
      else ctx.lineTo(x, y);
    });

    const lastX =
      padding.left +
      (chartW / Math.max(data.length - 1, 1)) * (data.length - 1);
    ctx.lineTo(lastX, padding.top + chartH);
    ctx.closePath();

    ctx.fillStyle = color + '30';
    ctx.fill();

    // Line
    ctx.beginPath();
    data.forEach((bucket, i) => {
      const x = padding.left + (chartW / Math.max(data.length - 1, 1)) * i;
      const y =
        padding.top + chartH - (bucket[metricKey] / maxValue.value) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
  });
};

onMounted(drawChart);
watch(() => [props.timeline, activeMetrics.value], drawChart, { deep: true });
</script>

<template>
  <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-2">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-trending-up size-4 text-n-slate-10" />
        <span
          class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
        >
          {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TIMELINE.TITLE') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-for="metric in metrics"
          :key="metric.key"
          type="button"
          class="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors cursor-pointer"
          :class="
            activeMetrics.includes(metric.key)
              ? 'bg-n-alpha-3 text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-11'
          "
          @click="toggleMetric(metric.key)"
        >
          <span
            class="size-2 rounded-full"
            :style="{ backgroundColor: metric.color }"
          />
          {{ metric.label }}
        </button>
      </div>
    </div>
    <div class="relative w-full">
      <canvas ref="canvasRef" class="w-full" />
    </div>
  </div>
</template>
